import Card from "components/card";
import { MdFeedback, MdChat, MdVideoCall, MdInsights, MdThumbUp, MdThumbDown } from "react-icons/md";
import { useState } from "react";

const MentorshipAnalyticsHub = () => {
  // Dummy mentorship data for 5k community
  const [mentorshipData] = useState({
    // Feedback data
    feedback: {
      totalFeedbacks: 4127,
      thumbsUp: 3654,
      thumbsDown: 473,
      recentComments: [
        { id: 1, positive: true, comment: "Exceptional guidance throughout my internship search", category: "Career" },
        { id: 2, positive: true, comment: "Very helpful with technical interview prep", category: "Technical" },
        { id: 3, positive: false, comment: "Session was too short", category: "General" }
      ]
    },
    // Conversation takeaways
    conversationTakeaways: {
      totalConversations: 11234,
      avgMessagesPerConversation: 18,
      topTopics: [
        { topic: "Career Guidance", count: 2947, percentage: 26 },
        { topic: "Technical Skills", count: 2357, percentage: 21 },
        { topic: "Interview Prep", count: 1798, percentage: 16 },
        { topic: "Networking", count: 1572, percentage: 14 }
      ]
    },
    // Virtual meeting takeaways
    virtualMeetings: {
      totalMeetings: 1634,
      avgDuration: 39, // minutes
      completionRate: 89.2,
      topOutcomes: [
        { outcome: "Goal Setting", count: 507, percentage: 31 },
        { outcome: "Skill Development", count: 392, percentage: 24 },
        { outcome: "Project Review", count: 327, percentage: 20 },
        { outcome: "Career Planning", count: 245, percentage: 15 }
      ]
    }
  });

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  return (
    <Card extra={"w-full h-fit px-6 pt-6 pb-6 mt-5"}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
          Mentorship Analytics Hub
        </h4>
        <MdInsights className="text-2xl text-brand-500" />
      </div>

      {/* Horizontal layout for three vertical blocks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Feedback Block */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 h-full">
          <div className="flex items-center gap-2 mb-4">
            <MdFeedback className="text-purple-500 text-lg" />
            <h5 className="text-base font-bold text-navy-700 dark:text-white">Feedback Overview</h5>
          </div>
          
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-navy-700 dark:text-white mb-1">
                {formatNumber(mentorshipData.feedback.totalFeedbacks)}
              </div>
              <div className="text-sm text-gray-500">Total Reviews</div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="text-center p-2 bg-white dark:bg-navy-800 rounded">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <MdThumbUp className="text-green-500 text-base" />
                  <div className="text-xl font-bold text-navy-700 dark:text-white">
                    {formatNumber(mentorshipData.feedback.thumbsUp)}
                  </div>
                </div>
                <div className="text-sm text-gray-500">Positive</div>
              </div>
              <div className="text-center p-2 bg-white dark:bg-navy-800 rounded">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <MdThumbDown className="text-gray-500 text-base" />
                  <div className="text-xl font-bold text-navy-700 dark:text-white">
                    {formatNumber(mentorshipData.feedback.thumbsDown)}
                  </div>
                </div>
                <div className="text-sm text-gray-500">Negative</div>
              </div>
            </div>
            
            <div className="text-center">
              <span className="text-base font-bold text-green-600">
                {Math.round((mentorshipData.feedback.thumbsUp / mentorshipData.feedback.totalFeedbacks) * 100)}% Positive Rate
              </span>
            </div>
          </div>
        </div>

        {/* Conversation Block */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 h-full">
          <div className="flex items-center gap-2 mb-4">
            <MdChat className="text-blue-500 text-lg" />
            <h5 className="text-base font-bold text-navy-700 dark:text-white">Conversation Insights</h5>
          </div>
          
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-navy-700 dark:text-white mb-1">
                {formatNumber(mentorshipData.conversationTakeaways.totalConversations * mentorshipData.conversationTakeaways.avgMessagesPerConversation)}
              </div>
              <div className="text-sm text-gray-500">Total Messages Sent</div>
            </div>
            
            <div className="space-y-2">
              <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Top Topics:</h6>
              {mentorshipData.conversationTakeaways.topTopics.slice(0, 3).map((topic, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{topic.topic}</span>
                  <span className="font-bold text-navy-700 dark:text-white">{topic.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Virtual Meeting Block */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 h-full">
          <div className="flex items-center gap-2 mb-4">
            <MdVideoCall className="text-green-500 text-lg" />
            <h5 className="text-base font-bold text-navy-700 dark:text-white">Virtual Meetings</h5>
          </div>
          
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-navy-700 dark:text-white mb-1">
                {formatNumber(mentorshipData.virtualMeetings.totalMeetings * mentorshipData.virtualMeetings.avgDuration)}
              </div>
              <div className="text-sm text-gray-500">Total Minutes</div>
            </div>
            
            <div className="space-y-2">
              <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Outcomes:</h6>
              {mentorshipData.virtualMeetings.topOutcomes.slice(0, 3).map((outcome, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{outcome.outcome}</span>
                  <span className="font-bold text-navy-700 dark:text-white">{outcome.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default MentorshipAnalyticsHub;